'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import type { NavigationContext } from '@/src/lib/navigation';
import { showToast } from '@/src/components/ui-kontora/use-toast';
import { hasPermission } from '@/src/lib/permissions';

export interface QuickAction {
  id: string;
  label: string;
  href: string;
  context: NavigationContext;
  description?: string;
  shortcut?: string;
  icon?: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { id: 'qa-invoice', label: 'Neue Rechnung', href: '/verkauf/rechnungen/neu', context: 'operations', description: 'Verkaufsrechnung erstellen', shortcut: 'N' },
  { id: 'qa-receipt', label: 'Neuer Beleg', href: '/dokumente/upload', context: 'operations', description: 'Eingangsbeleg erfassen', shortcut: 'B' },
  { id: 'qa-customer', label: 'Neuer Kunde', href: '/stammdaten/partner?neu=1', context: 'operations', description: 'Partner / Kunde anlegen' },
  { id: 'qa-product', label: 'Neues Produkt', href: '/stammdaten/produkte?neu=1', context: 'operations', description: 'Produkt / Artikel' },
  { id: 'qa-booking', label: 'Buchung', href: '/buchhaltung/buchungsvorschlaege', context: 'accounting', description: 'Buchungsvorschläge' },
  { id: 'qa-bank', label: 'Bankimport', href: '/importe', context: 'accounting', description: 'CSV / CAMT importieren', shortcut: 'I' },
  { id: 'qa-cashbook', label: 'Kassenbuch', href: '/buchhaltung/kassenbuch', context: 'accounting', description: 'Kassenbewegungen' },
  { id: 'qa-report', label: 'Bericht', href: '/reports', context: 'accounting', description: 'Reports öffnen' },
  { id: 'qa-task', label: 'Aufgabe', href: '/aufgaben', context: 'overview', description: 'Eigene Aufgaben' },
  { id: 'qa-invite', label: 'Benutzer einladen', href: '/einstellungen/benutzer-rollen', context: 'tenant', description: 'Mitglieder einladen' },
  { id: 'qa-cmd-k', label: 'Befehlspalette', href: '#cmd-k', context: 'overview', description: 'Schnellzugriff via Cmd+K', shortcut: '⌘K' },
];

export interface QuickActionsBarProps {
  activeContext: NavigationContext;
  permissions?: readonly string[];
  placement?: 'floating' | 'inline';
}

const ACTION_PERMISSIONS: Record<string, string> = {
  'qa-invoice': 'invoice.create',
  'qa-receipt': 'document.upload',
  'qa-customer': 'invoice.create',
  'qa-product': 'invoice.create',
  'qa-booking': 'booking.read',
  'qa-bank': 'bank_statement.import',
  'qa-cashbook': 'cashbook.read',
  'qa-report': 'report.read',
  'qa-invite': 'user.invite',
};

export function QuickActionsBar({ activeContext, permissions = [], placement = 'floating' }: QuickActionsBarProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const allowed = QUICK_ACTIONS.filter((action) => hasPermission(permissions, ACTION_PERMISSIONS[action.id]));
  const relevant = allowed.filter((action) => action.context === activeContext);
  const other = allowed.filter((action) => action.context !== activeContext);

  function handleActionClick(action: QuickAction) {
    if (action.id === 'qa-cmd-k') {
      window.dispatchEvent(new CustomEvent('kontora:open-command-palette'));
      showToast({ variant: 'info', title: 'Befehlspalette geöffnet', description: 'Tippe einen Befehl oder suche…' });
      return;
    }
    showToast({
      variant: 'info',
      title: `Öffne ${action.label}`,
      description: action.description ?? 'Wird vorbereitet…',
    });
  }

  return (
    <div
      ref={ref}
      data-tour="quick-actions"
      className={placement === 'inline' ? 'relative shrink-0 print:hidden' : 'fixed bottom-[calc(9rem+env(safe-area-inset-bottom))] right-6 z-30 flex flex-col items-end gap-2 lg:bottom-24 print:hidden'}
    >
      {open ? (
        <div className={`w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)] ${placement === 'inline' ? 'absolute left-0 top-full z-50 mt-2 max-h-[min(28rem,calc(100dvh-20rem))] sm:left-auto sm:right-0' : ''}`}>
          <div className="border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
              Schnellaktionen · {activeContext}
            </p>
          </div>
          {relevant.length > 0 ? (
            <ul className="p-2">
              {relevant.map((action) => (
                <li key={action.id}>
                  <Link
                    href={action.href}
                    onClick={() => {
                      handleActionClick(action);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-primary)] transition hover:bg-[var(--k-color-accent-soft)] hover:text-[var(--k-color-text-link)]"
                  >
                    <span className="flex flex-col">
                      <span className="font-semibold">{action.label}</span>
                      {action.description ? (
                        <span className="mt-0.5 text-[11px] font-normal text-[var(--k-color-text-muted)]">
                          {action.description}
                        </span>
                      ) : null}
                    </span>
                    {action.shortcut ? (
                      <kbd className="rounded bg-[var(--k-color-surface-muted)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--k-color-text-muted)]">
                        {action.shortcut}
                      </kbd>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {other.length > 0 ? (
            <>
              <div className="border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
                  Weitere
                </p>
              </div>
              <ul className="p-2">
                {other.slice(0, 4).map((action) => (
                  <li key={action.id}>
                    <Link
                      href={action.href}
                      onClick={() => {
                        handleActionClick(action);
                        setOpen(false);
                      }}
                      className="flex items-center justify-between rounded-[var(--k-radius-sm)] px-3 py-2 text-xs text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)]"
                    >
                      <span className="truncate">{action.label}</span>
                      <span className="ml-2 shrink-0 text-[10px] uppercase tracking-wider text-[var(--k-color-text-faint)]">
                        {action.context}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <div className="border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-1.5 text-[10px] text-[var(--k-color-text-muted)]">
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">N</kbd> Rechnung ·{' '}
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">B</kbd> Beleg ·{' '}
            <kbd className="rounded bg-[var(--k-color-surface)] px-1 font-mono">⌘K</kbd> Palette
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Schnellaktionen"
        title="Schnellaktionen (N)"
        className={placement === 'inline' ? 'inline-flex min-h-9 items-center justify-center gap-2 rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] px-2.5 py-2 text-sm font-semibold text-[var(--k-color-text-on-accent)] transition hover:bg-[var(--k-color-accent-strong)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--k-color-focus-ring)]' : 'inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--k-color-accent)] text-white shadow-[var(--k-shadow-md)] transition hover:bg-[var(--k-color-accent-strong)] hover:scale-105'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`transition-transform duration-200 ${open ? 'rotate-45' : ''}`}
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {placement === 'inline' ? <span className="hidden sm:inline">Schnellaktionen</span> : null}
      </button>
    </div>
  );
}

export function useQuickActionShortcut(activeContext: NavigationContext, permissions: readonly string[] = []) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
        if (e.target.isContentEditable) return;
      }
      if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        const next = QUICK_ACTIONS.find((action) => action.context === activeContext && action.id === 'qa-invoice' && hasPermission(permissions, ACTION_PERMISSIONS[action.id]));
        if (next) {
          showToast({ variant: 'info', title: `Öffne ${next.label}` });
          window.location.href = next.href;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeContext, permissions]);
}
